import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  FileText, Download, TrendingUp, TrendingDown, ChevronRight, 
  HelpCircle, Briefcase, MoreHorizontal, Copy, CheckSquare,
  Search, Filter, Plus, CheckCircle, Phone, Eye, Trash2, Calendar, User
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import AdvancedQuoteCreator from './AdvancedQuoteCreator';
import QuoteView from './QuoteView';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function QuotesManagement() {
  const [quotes, setQuotes] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showCreateQuoteDialog, setShowCreateQuoteDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showQuoteViewDialog, setShowQuoteViewDialog] = useState(false);
  const [autoOpenConvert, setAutoOpenConvert] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [convertData, setConvertData] = useState({
    handyman_id: '',
    scheduled_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, handymenRes] = await Promise.all([
        axiosInstance.get('/quotes'),
        axiosInstance.get('/handymen')
      ]);
      
      setQuotes(quotesRes.data);
      setHandymen(handymenRes.data.filter(h => h.is_available));
      
      // Calculate dashboard stats
      calculateDashboardStats(quotesRes.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const calculateDashboardStats = (quotesData) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    // Count by status
    const draft = quotesData.filter(q => q.status === 'draft').length;
    const awaitingResponse = quotesData.filter(q => q.status === 'sent').length;
    const changesRequested = quotesData.filter(q => q.status === 'changes_requested').length;
    const approved = quotesData.filter(q => q.status === 'approved').length;
    const converted = quotesData.filter(q => q.status === 'converted' || q.status === 'accepted' || q.status === 'job_completed').length;

    // Quotes sent in past 30 days
    const sentLast30 = quotesData.filter(q => 
      q.sent_at && new Date(q.sent_at) >= thirtyDaysAgo
    );
    const sentPrev30 = quotesData.filter(q => 
      q.sent_at && new Date(q.sent_at) >= sixtyDaysAgo && new Date(q.sent_at) < thirtyDaysAgo
    );

    const sentCount = sentLast30.length;
    const sentValue = sentLast30.reduce((sum, q) => sum + (q.total_amount || 0), 0);
    const sentPrevCount = sentPrev30.length;
    const sentChange = sentPrevCount > 0 ? Math.round(((sentCount - sentPrevCount) / sentPrevCount) * 100) : (sentCount > 0 ? 100 : 0);

    // Converted in past 30 days
    const convertedLast30 = quotesData.filter(q => 
      (q.status === 'converted' || q.status === 'accepted' || q.status === 'job_completed') && q.created_at && new Date(q.created_at) >= thirtyDaysAgo
    );
    const convertedPrev30 = quotesData.filter(q => 
      (q.status === 'converted' || q.status === 'accepted' || q.status === 'job_completed') && q.created_at && new Date(q.created_at) >= sixtyDaysAgo && new Date(q.created_at) < thirtyDaysAgo
    );

    const convertedCount = convertedLast30.length;
    const convertedValue = convertedLast30.reduce((sum, q) => sum + (q.total_amount || 0), 0);
    const convertedPrevCount = convertedPrev30.length;
    const convertedChange = convertedPrevCount > 0 ? Math.round(((convertedCount - convertedPrevCount) / convertedPrevCount) * 100) : (convertedCount > 0 ? 100 : 0);

    // Conversion rate
    const conversionRate = sentCount > 0 ? Math.round((convertedCount / sentCount) * 100) : 0;
    const prevConversionRate = sentPrevCount > 0 ? Math.round((convertedPrevCount / sentPrevCount) * 100) : 0;
    const conversionChange = conversionRate - prevConversionRate;

    setDashboardStats({
      overview: {
        draft,
        awaitingResponse,
        changesRequested,
        approved
      },
      conversionRate: {
        rate: conversionRate,
        change: conversionChange,
        period: 'Past 30 days'
      },
      sent: {
        count: sentCount,
        value: sentValue,
        change: sentChange,
        period: 'Past 30 days'
      },
      converted: {
        count: convertedCount,
        value: convertedValue,
        change: convertedChange,
        period: 'Past 30 days'
      }
    });
  };

  const handleConvertToJob = async () => {
    if (!convertData.handyman_id || !convertData.scheduled_date) {
      toast.error('Please select handyman and schedule date');
      return;
    }

    try {
      await axiosInstance.post(`/quotes/${selectedQuote.id}/convert-to-job`, {
        handyman_id: convertData.handyman_id,
        scheduled_date: new Date(convertData.scheduled_date).toISOString()
      });
      
      toast.success('Quote converted to job successfully!');
      setShowConvertDialog(false);
      setSelectedQuote(null);
      setConvertData({ handyman_id: '', scheduled_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to convert quote to job');
    }
  };

  const handleDownloadPDF = async (quote, e) => {
    e?.stopPropagation();
    try {
      const response = await axiosInstance.get(`/quotes/${quote.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${quote.quote_number || quote.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Admin approve quote on behalf of customer (e.g., phone approval)
  const [approvalMethod, setApprovalMethod] = useState('other_communication');
  
  const handleAdminApproveQuote = async () => {
    if (!selectedQuote) return;
    
    // Get current admin user info
    const adminUser = JSON.parse(localStorage.getItem('user') || '{}');
    
    setApproving(true);
    try {
      const response = await axiosInstance.post(`/quotes/${selectedQuote.id}/admin-approve`, {
        approval_method: approvalMethod,
        notes: approveNote,
        admin_name: adminUser.name || adminUser.email || 'Admin User',
        admin_email: adminUser.email || 'admin@system'
      });
      
      toast.success('Quote approved successfully! Job has been created.');
      setShowApproveDialog(false);
      setSelectedQuote(null);
      setApproveNote('');
      setApprovalMethod('other_communication');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setApproving(false);
    }
  };

  const handleQuoteClick = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteViewDialog(true); // Open View Mode by default
  };

  const handleEditQuote = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteViewDialog(false);
    setShowQuoteDialog(true); // Open Edit Mode
  };

  const handleDeleteQuote = async () => {
    if (!selectedQuote) return;
    
    setDeleting(true);
    try {
      // Use force=true to delete non-draft quotes
      await axiosInstance.delete(`/quotes/${selectedQuote.id}?force=true`);
      toast.success('Quote deleted successfully');
      setShowDeleteDialog(false);
      setSelectedQuote(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      converted: 'bg-blue-100 text-blue-700',
      accepted: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      changes_requested: 'bg-orange-100 text-orange-700',
      job_completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Awaiting response',
      approved: 'Approved',
      converted: 'Converted to Job',
      accepted: 'Converted to Job',
      rejected: 'Rejected',
      changes_requested: 'Changes requested',
      job_completed: 'Job Completed',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatCurrency = (value) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Filter quotes
  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = searchTerm === '' || 
      quote.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.customer_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.service_address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div data-testid="quotes-management">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Quotes
          </h2>
          <p className="text-gray-600">Manage and track customer quotes</p>
        </div>
        <Button 
          onClick={() => setShowCreateQuoteDialog(true)}
          className="bg-[#595959] hover:bg-[#404040]"
          data-testid="create-quote-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Quote
        </Button>
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Overview Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Overview</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                  <span className="text-gray-700">Draft ({dashboardStats.overview.draft})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  <span className="text-gray-700">Awaiting response ({dashboardStats.overview.awaitingResponse})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span className="text-gray-700">Changes requested ({dashboardStats.overview.changesRequested})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-gray-700">Approved ({dashboardStats.overview.approved})</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Rate Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Conversion rate</h3>
                <div className="flex items-center gap-1">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">{dashboardStats.conversionRate.period}</p>
              <div className="flex items-end gap-3">
                <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {dashboardStats.conversionRate.rate}%
                </span>
                <span className={`text-sm px-2 py-1 rounded-full flex items-center gap-1 ${
                  dashboardStats.conversionRate.change >= 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {dashboardStats.conversionRate.change >= 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {dashboardStats.conversionRate.change >= 0 ? '' : ''}{dashboardStats.conversionRate.change}%
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Sent Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Sent</h3>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mb-4">{dashboardStats.sent.period}</p>
              <div className="flex items-end gap-3">
                <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {dashboardStats.sent.count}
                </span>
                <span className={`text-sm px-2 py-1 rounded-full flex items-center gap-1 ${
                  dashboardStats.sent.change >= 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {dashboardStats.sent.change >= 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {dashboardStats.sent.change >= 0 ? '+' : ''}{dashboardStats.sent.change}%
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">{formatCurrency(dashboardStats.sent.value)}</p>
            </CardContent>
          </Card>

          {/* Converted Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Converted</h3>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mb-4">{dashboardStats.converted.period}</p>
              <div className="flex items-end gap-3">
                <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {dashboardStats.converted.count}
                </span>
                <span className={`text-sm px-2 py-1 rounded-full flex items-center gap-1 ${
                  dashboardStats.converted.change >= 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {dashboardStats.converted.change >= 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {dashboardStats.converted.change >= 0 ? '+' : ''}{dashboardStats.converted.change}%
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">{formatCurrency(dashboardStats.converted.value)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by client, quote number, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Awaiting response</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quotes Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No quotes found</p>
            <p className="text-gray-400 text-sm">Create quotes from the Leads section</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-4 font-medium text-gray-600">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" className="rounded border-gray-300" />
                      Client
                    </div>
                  </th>
                  <th className="text-left p-4 font-medium text-gray-600">Quote number</th>
                  <th className="text-left p-4 font-medium text-gray-600">Property</th>
                  <th className="text-left p-4 font-medium text-gray-600">Created</th>
                  <th className="text-left p-4 font-medium text-gray-600">Status</th>
                  <th className="text-right p-4 font-medium text-gray-600">Total</th>
                  <th className="text-center p-4 font-medium text-gray-600 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote, index) => (
                  <tr 
                    key={quote.id} 
                    className={`border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                    onClick={() => handleQuoteClick(quote)}
                    data-testid={`quote-row-${quote.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          className="rounded border-gray-300" 
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="font-medium text-gray-900">{quote.customer_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <span className="font-medium text-gray-900">#{quote.quote_number?.split('-').pop() || quote.id.slice(0, 6).toUpperCase()}</span>
                        <p className="text-sm text-gray-500 truncate max-w-[200px]">
                          {quote.service_type || quote.items?.[0]?.name || 'Service'}
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-700 truncate max-w-[250px]">
                        {quote.service_address || quote.customer_address || 'N/A'}
                      </p>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-700">
                        {new Date(quote.created_at).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </span>
                    </td>
                    <td className="p-4">
                      <Badge className={`${getStatusColor(quote.status)} font-normal`}>
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          quote.status === 'sent' ? 'bg-yellow-500' :
                          quote.status === 'approved' ? 'bg-green-500' :
                          quote.status === 'accepted' ? 'bg-blue-500' :
                          quote.status === 'rejected' ? 'bg-red-500' :
                          'bg-gray-400'
                        }`}></span>
                        {getStatusLabel(quote.status)}
                      </Badge>
                      {quote.use_package && (
                        <Badge className="ml-1 bg-blue-100 text-blue-800 text-xs font-normal">PKG</Badge>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-semibold text-gray-900">
                        ${quote.total_amount?.toLocaleString('en-US', { timeZone: 'America/Phoenix', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuote(quote);
                            setShowQuoteViewDialog(true);
                          }}
                          title="View Quote"
                          data-testid={`view-quote-${quote.id}`}
                        >
                          <Eye className="w-4 h-4 text-[#595959]" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={(e) => handleDownloadPDF(quote, e)}
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4 text-gray-500" />
                        </Button>
                        {(quote.status === 'sent' || quote.status === 'revision_requested') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedQuote(quote);
                              setShowApproveDialog(true);
                            }}
                            title="Approve Quote (Phone Approval)"
                            data-testid={`approve-quote-${quote.id}`}
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        {quote.status === 'approved' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedQuote(quote);
                              setAutoOpenConvert(true);
                              setShowQuoteViewDialog(true);
                            }}
                            title="Convert to Job"
                          >
                            <Briefcase className="w-4 h-4 text-[#595959]" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuote(quote);
                            setShowDeleteDialog(true);
                          }}
                          title="Delete Quote"
                          data-testid={`delete-quote-${quote.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Quote
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this quote? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedQuote && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium">{selectedQuote.customer_name}</p>
              <p className="text-sm text-gray-500">Quote #{selectedQuote.quote_number}</p>
              <p className="text-sm text-gray-500">Amount: ${selectedQuote.total_amount?.toFixed(2)}</p>
              <Badge className={`mt-2 ${getStatusColor(selectedQuote.status)}`}>
                {getStatusLabel(selectedQuote.status)}
              </Badge>
            </div>
          )}
          <div className="flex gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)} 
              className="flex-1"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteQuote}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={deleting}
              data-testid="confirm-delete-quote"
            >
              {deleting ? 'Deleting...' : 'Delete Quote'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote View Dialog - Opens in read-only mode when clicking on a quote */}
      <QuoteView
        open={showQuoteViewDialog}
        onOpenChange={(open) => {
          setShowQuoteViewDialog(open);
          if (!open) {
            setSelectedQuote(null);
            setAutoOpenConvert(false);
          }
        }}
        quoteId={selectedQuote?.id}
        onEdit={handleEditQuote}
        onRefresh={fetchData}
        autoOpenConvert={autoOpenConvert}
      />

      {/* Advanced Quote Dialog - Opens in edit mode */}
      <AdvancedQuoteCreator
        open={showQuoteDialog}
        onOpenChange={(open) => {
          setShowQuoteDialog(open);
          if (!open) setSelectedQuote(null);
        }}
        lead={null}
        existingQuote={selectedQuote}
        onSuccess={fetchData}
      />

      {/* Create New Quote Dialog - Opens from Create Quote button */}
      <AdvancedQuoteCreator
        open={showCreateQuoteDialog}
        onOpenChange={setShowCreateQuoteDialog}
        lead={null}
        existingQuote={null}
        onSuccess={fetchData}
        enableCustomerSelection={true}
      />

      {/* Convert to Job Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#595959]">
              <Briefcase className="h-5 w-5" />
              Convert Quote to Job
            </DialogTitle>
            <DialogDescription>
              Customer has approved this quote. Assign a handyman and schedule the job.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedQuote && (
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-[#595959]" />
                  <span className="font-semibold text-teal-800">Quote Details</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Quote #</p>
                    <p className="font-medium text-gray-900">{selectedQuote.quote_number || `QT-${selectedQuote.id?.substring(0, 8)}`}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Amount</p>
                    <p className="font-bold text-[#595959]">${selectedQuote.total_amount?.toLocaleString()}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Customer</p>
                    <p className="font-medium text-gray-900">{selectedQuote.customer_name}</p>
                  </div>
                  {selectedQuote.property_address && (
                    <div className="col-span-2">
                      <p className="text-gray-500">Service Location</p>
                      <p className="font-medium text-gray-900 text-xs">{selectedQuote.property_address}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Assign Handyman <span className="text-red-500">*</span>
                </Label>
                <Select value={convertData.handyman_id} onValueChange={(value) => setConvertData({...convertData, handyman_id: value})}>
                  <SelectTrigger className="mt-1.5" data-testid="convert-handyman-select">
                    <SelectValue placeholder="Select a handyman..." />
                  </SelectTrigger>
                  <SelectContent>
                    {handymen.map((handyman) => (
                      <SelectItem key={handyman.id} value={handyman.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-3 h-3 text-[#595959]" />
                          </div>
                          <div>
                            <span className="font-medium">{handyman.name}</span>
                            {handyman.skills?.length > 0 && (
                              <span className="text-xs text-gray-500 ml-2">
                                • {handyman.skills.slice(0, 2).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Schedule Date & Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="datetime-local"
                  className="mt-1.5"
                  value={convertData.scheduled_date}
                  onChange={(e) => setConvertData({...convertData, scheduled_date: e.target.value})}
                  data-testid="convert-schedule-date"
                />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-medium text-blue-800 mb-1">What happens next:</p>
              <ul className="text-xs text-blue-700 space-y-0.5">
                <li>• Job will be created with all quote details</li>
                <li>• Handyman will receive notification and see this in their portal</li>
                <li>• Customer can track job progress in their portal</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowConvertDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConvertToJob} 
                className="flex-1 bg-[#595959] hover:bg-[#404040] text-white"
                disabled={!convertData.handyman_id || !convertData.scheduled_date}
                data-testid="confirm-convert-to-job"
              >
                <Briefcase size={16} className="mr-2" />
                Create Job & Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Approve Quote Dialog (Phone Approval) */}
      <Dialog open={showApproveDialog} onOpenChange={(open) => {
        setShowApproveDialog(open);
        if (!open) {
          setApproveNote('');
        setApprovalMethod('other_communication');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Approve Quote
            </DialogTitle>
            <DialogDescription>
              Record approval on behalf of a customer who confirmed via phone, email, or other channel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedQuote && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-900 mb-2">
                  <strong>Quote Details:</strong>
                </p>
                <div className="text-sm text-green-800 space-y-1">
                  <p>Quote #: {selectedQuote.quote_number || selectedQuote.id?.substring(0, 8).toUpperCase()}</p>
                  <p>Customer: {selectedQuote.customer_name}</p>
                  <p>Email: {selectedQuote.customer_email}</p>
                  <p>Phone: {selectedQuote.customer_phone || 'N/A'}</p>
                  <p className="font-semibold text-green-900 pt-1">
                    Total: ${selectedQuote.total_amount?.toLocaleString('en-US', { timeZone: 'America/Phoenix', minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label>Approval Method *</Label>
              <Select value={approvalMethod} onValueChange={setApprovalMethod}>
                <SelectTrigger className="mt-1" data-testid="approval-method-select">
                  <SelectValue placeholder="Select how customer approved" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="other_communication">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Other Communication (Phone, In-Person, etc.)
                    </div>
                  </SelectItem>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Email Confirmation
                    </div>
                  </SelectItem>
                  <SelectItem value="portal">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="w-4 h-4" /> Portal (Admin Action)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Approval Notes</Label>
              <Textarea
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="E.g., Customer confirmed via phone call on [date], spoke with [contact name]..."
                rows={3}
                className="mt-1"
                data-testid="approval-notes-input"
              />
              <p className="text-xs text-gray-500 mt-1">Add details about how and when approval was received</p>
            </div>

            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-900">
                <strong>What happens next:</strong>
                <br />• Quote status changes to "Approved"
                <br />• Approval details are recorded in history
                <br />• Job is automatically created in pending status
                <br />• You can then assign a handyman and schedule the job
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowApproveDialog(false);
                  setApproveNote('');
                  setApprovalMethod('other_communication');
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAdminApproveQuote} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={approving}
                data-testid="confirm-admin-approve"
              >
                {approving ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Confirm & Approve
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
